import 'twin.macro'

import { useAppDispatch, useAppSelector } from 'hooks'
import { useForensics } from 'hooks/useForensics'
import { Fragment, useEffect, useReducer, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useLazyGetApplicationStatusQuery } from 'redux/api'
import {
  useDidBiometryRetryMutation,
  useDidDamuSignMainDocsMutation,
  useDidPledgeSignMainDocsMutation,
  useDidSignMainDocsMutation,
  useSignDidBiometryRepaymentScheduleMutation,
  useStartForensicsMutation,
  useVerifyForensicsMutation
} from 'redux/api/biometryApi'
import { getStatusSelector } from 'redux/store/slices/statusSlice'
import { getCurrentFlowId } from 'redux/store/slices/userSlice'
import { getNotificationsSelector, removeStepNotificationsData } from 'redux/store/slices/wsSlice'
import { FLOW_STEPS, StepNotification } from 'share/types'
import { type CurrentStepBody } from 'share/types/api/main'
import { pathnames } from 'utils/constants'

import { BiometryMainBlock, BiometryModal, BiometryQRBlock } from './template'

type BiometryProps = {
  qrValue: string
}

type AITUSigningState = {
  isLoading: boolean
  isLinkGained: boolean
  isError: boolean
  isSuccess: boolean
  link?: string
}

const initAITUSigningState: AITUSigningState = {
  isLinkGained: false,
  isLoading: false,
  isError: false,
  isSuccess: false,
  link: undefined
}

const DOCS_SIGNING_STEPS: Array<FLOW_STEPS> = [
  FLOW_STEPS.SIGN_DOCS_AITU,
  FLOW_STEPS.SIGN_DOCS_AITU_DAMU,
  FLOW_STEPS.SIGN_DOCS_AITU_PLEDGE,
  FLOW_STEPS.SIGN_REPAYMENT_SCHEDULE_WITH_AITU
]
const AITUSigningReducer = (prev: AITUSigningState, next: Partial<AITUSigningState>) => ({
  ...prev,
  ...next
})

const openWindow = (url: string) => {
  const theme = localStorage.getItem('color-theme')
  const urlWithTheme = `${url}${theme ? `&theme=${theme}` : ''}`
  window.location.replace(urlWithTheme)
}

const checkStep = ({
  currentStep,
  nextStep,
  matchingStep
}: {
  currentStep: CurrentStepBody | null
  nextStep: keyof typeof FLOW_STEPS
  matchingStep: FLOW_STEPS
}) => {
  if (
    nextStep === matchingStep ||
    (currentStep && currentStep.step === matchingStep && currentStep.status === 'FAILED')
  )
    return true
  return false
}

const Biometry = ({ qrValue }: BiometryProps) => {
  // Tools
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  // Selectors | Store
  const currentFlowId = useAppSelector(getCurrentFlowId)
  const { SIGN_DOCS_AITU, SIGN_DOCS_AITU_DAMU, SIGN_REPAYMENT_SCHEDULE_WITH_AITU, SIGN_DOCS_AITU_PLEDGE } =
    useAppSelector(getNotificationsSelector)
  const { current_step, next_step } = useAppSelector(getStatusSelector)

  // State
  const [isCondOpen, setIsCondOpen] = useState(false)
  const [AITUSigningState, setAITUSigningState] = useReducer(AITUSigningReducer, initAITUSigningState)

  // Mutations
  const [fetchStatus, { isLoading: isStatusLoading }] = useLazyGetApplicationStatusQuery()
  const [startForensics, { isLoading: isForensicsLoading }] = useStartForensicsMutation()
  const [verifyForensics, { isLoading: isForensicsVerifyLoading }] = useVerifyForensicsMutation()
  const [signDidRepaymentDocs, { isLoading: isDidSignRepaymentDocsLoading }] =
    useSignDidBiometryRepaymentScheduleMutation()
  const [signDidMainDocs, { isLoading: isDidSignMainDocsLoading }] = useDidSignMainDocsMutation()
  const [signDidDamuMainDocs, { isLoading: isDidSignDamuMainDocsLoading }] = useDidDamuSignMainDocsMutation()
  const [didBiometryRetry, { isLoading: isDidBiometryRetryLoading }] = useDidBiometryRetryMutation()
  const [signDidPledgeMainDocs, { isLoading: isSignDidPledgeMainDocs }] = useDidPledgeSignMainDocsMutation()

  // Computed values
  const isLoading =
    AITUSigningState.isLoading ||
    isStatusLoading ||
    isForensicsLoading ||
    isForensicsVerifyLoading ||
    isDidSignRepaymentDocsLoading ||
    isDidSignMainDocsLoading ||
    isDidSignDamuMainDocsLoading ||
    isDidBiometryRetryLoading ||
    isSignDidPledgeMainDocs

  // Handlers
  const handleBiometryStart = async () => {
    if (!currentFlowId) return

    dispatch(
      removeStepNotificationsData([
        FLOW_STEPS.SIGN_DOCS_AITU,
        FLOW_STEPS.SIGN_DOCS_AITU_DAMU,
        FLOW_STEPS.SIGN_DOCS_AITU_PLEDGE,
        FLOW_STEPS.SIGN_REPAYMENT_SCHEDULE_WITH_AITU
      ])
    )
    setIsCondOpen(false)

    const response = await fetchStatus(currentFlowId).unwrap()
    const { id, next_step, current_step } = response

    if ([next_step, current_step?.step].includes(FLOW_STEPS.BIOMETRY_FORENSICS)) {
      const { step_id } = await startForensics(id).unwrap()
      runOzLiveness(step_id, FLOW_STEPS.BIOMETRY_FORENSICS)
      return
    }
    if (checkStep({ currentStep: current_step, nextStep: next_step, matchingStep: FLOW_STEPS.SIGN_DOCS_AITU })) {
      await signDidMainDocs(id).unwrap()
      return
    }
    if (checkStep({ currentStep: current_step, nextStep: next_step, matchingStep: FLOW_STEPS.SIGN_DOCS_AITU_DAMU })) {
      await signDidDamuMainDocs(id).unwrap()
      return
    }
    if (
      checkStep({
        currentStep: current_step,
        nextStep: next_step,
        matchingStep: FLOW_STEPS.SIGN_REPAYMENT_SCHEDULE_WITH_AITU
      })
    ) {
      await signDidRepaymentDocs(id).unwrap()
      return
    }
    if (checkStep({ currentStep: current_step, nextStep: next_step, matchingStep: FLOW_STEPS.SIGN_DOCS_AITU_PLEDGE })) {
      await signDidPledgeMainDocs(id).unwrap()
      return
    }
    if (!current_step) return

    if (DOCS_SIGNING_STEPS.includes(current_step.step as FLOW_STEPS)) {
      await handleDidBiometryRetry()
    }
  }

  const handleDidBiometryRetry = async () => {
    try {
      if (!currentFlowId) return
      const { current_step } = await fetchStatus(currentFlowId).unwrap()
      if (!current_step) return
      const { id: stepId } = current_step
      await didBiometryRetry(stepId).unwrap()
    } catch (err) {
      console.log(err)
    }
  }

  const onForensicsComplete = async () => {
    const OzLiveness = window.OzLiveness
    const { current_step } = await fetchStatus(currentFlowId as string).unwrap()
    if (!current_step) return

    const { id } = current_step
    await verifyForensics(id as string)
    OzLiveness.close()
  }

  const runOzLiveness = useForensics({
    onSubmit: () => {
      navigate(pathnames['scoring-loader'])
    },
    onComplete: onForensicsComplete
  })

  // Effects
  useEffect(() => {
    const stepsArray = [SIGN_DOCS_AITU, SIGN_DOCS_AITU_DAMU, SIGN_DOCS_AITU_PLEDGE, SIGN_REPAYMENT_SCHEDULE_WITH_AITU]
    const stepsData = stepsArray.filter((step): step is StepNotification => !!step)
    if (!current_step || !stepsData.length) return

    setAITUSigningState({ isLoading: true })
    const currentWsStep = stepsData.find(step => step?.value.step_type === current_step.step || next_step)

    if (!currentWsStep) return setAITUSigningState(initAITUSigningState)

    if (
      (currentWsStep.value.is_final && !currentWsStep.value.is_success) ||
      (currentWsStep.value.is_final && currentWsStep.value.is_success)
    ) {
      dispatch(
        removeStepNotificationsData([
          FLOW_STEPS.SIGN_DOCS_AITU,
          FLOW_STEPS.SIGN_DOCS_AITU_DAMU,
          FLOW_STEPS.SIGN_DOCS_AITU_PLEDGE,
          FLOW_STEPS.SIGN_REPAYMENT_SCHEDULE_WITH_AITU
        ])
      )
      return setAITUSigningState(initAITUSigningState)
    }
    const currentWsStepData = currentWsStep.value.data as {
      payload: {
        signing_url: string
      }
      success: boolean
      type: 'signing_notification'
    }
    if (!currentWsStepData || Object.keys(currentWsStepData).length === 0) return
    if (!currentWsStepData.success) {
      return setAITUSigningState({
        isError: true
      })
    }
    setAITUSigningState({
      isSuccess: true,
      isError: false,
      isLoading: false,
      isLinkGained: true,
      link: currentWsStepData.payload.signing_url
    })
    dispatch(
      removeStepNotificationsData([
        FLOW_STEPS.SIGN_DOCS_AITU,
        FLOW_STEPS.SIGN_DOCS_AITU_DAMU,
        FLOW_STEPS.SIGN_DOCS_AITU_PLEDGE,
        FLOW_STEPS.SIGN_REPAYMENT_SCHEDULE_WITH_AITU
      ])
    )
    setTimeout(() => {
      openWindow(currentWsStepData.payload.signing_url)
    }, 250)
  }, [
    SIGN_DOCS_AITU,
    SIGN_DOCS_AITU_DAMU,
    SIGN_DOCS_AITU_PLEDGE,
    SIGN_REPAYMENT_SCHEDULE_WITH_AITU,
    current_step,
    dispatch,
    next_step
  ])

  return (
    <Fragment>
      <BiometryMainBlock
        isLoading={isLoading}
        subtitle={
          next_step || current_step?.step === 'BIOMETRY_FORENSICS'
            ? t('flow-process-forensics')
            : t('flow-process-digital-id')
        }
        onButtonClick={() => setIsCondOpen(true)}
      />
      <BiometryQRBlock QRValue={qrValue} />
      <BiometryModal isOpen={isCondOpen} onOpenChange={setIsCondOpen} onContinueButtonClick={handleBiometryStart} />
    </Fragment>
  )
}

export default Biometry
